// Utility functions for safe localStorage operations

export const storage = {
  // Get item from localStorage with error handling
  getItem: (key, defaultValue = null) => {
    try {
      const item = localStorage.getItem(key);
      if (!item) return defaultValue;

      // Try to parse as JSON, but if it fails, return the raw string
      // This handles both JSON-stored values and plain string tokens
      try {
        return JSON.parse(item);
      } catch (parseError) {
        // If JSON.parse fails, it's likely a plain string (like a token)
        // Return it as-is
        return item;
      }
    } catch (error) {
      console.error(`Error reading ${key} from localStorage:`, error);
      return defaultValue;
    }
  },

  // Set item in localStorage with error handling
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error(`Error writing ${key} to localStorage:`, error);
      return false;
    }
  },

  // Remove item from localStorage with error handling
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Error removing ${key} from localStorage:`, error);
      return false;
    }
  },

  // Clear all auth-related data
  clearAuthData: () => {
    console.log("🗑️ clearAuthData called");
    console.trace("Stack trace:"); // This will show where it was called from
    const authKeys = ["token", "refreshToken", "user", "permissions"];
    authKeys.forEach((key) => {
      console.log(`  Removing ${key} from localStorage`);
      storage.removeItem(key);
    });
  },

  // Comprehensive clear - removes ALL browser storage
  clearAllStorage: async () => {
    console.log("🗑️ Comprehensive storage clear initiated");

    try {
      // 1. Clear localStorage
      console.log("  Clearing localStorage...");
      localStorage.clear();

      // 2. Clear sessionStorage
      console.log("  Clearing sessionStorage...");
      sessionStorage.clear();

      // 3. Clear all cookies
      console.log("  Clearing cookies...");
      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });

      // 4. Clear IndexedDB
      console.log("  Clearing IndexedDB...");
      if (window.indexedDB) {
        const databases = await window.indexedDB.databases();
        databases.forEach((db) => {
          if (db.name) {
            window.indexedDB.deleteDatabase(db.name);
            console.log(`    Deleted IndexedDB: ${db.name}`);
          }
        });
      }

      // 5. Clear Service Worker caches
      console.log("  Clearing Service Worker caches...");
      if ("caches" in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map((cacheName) => {
            console.log(`    Deleting cache: ${cacheName}`);
            return caches.delete(cacheName);
          }),
        );
      }

      // 6. Unregister service workers
      console.log("  Unregistering Service Workers...");
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map((registration) => {
            console.log(`    Unregistering SW: ${registration.scope}`);
            return registration.unregister();
          }),
        );
      }

      console.log("✅ Comprehensive storage clear completed");
      return true;
    } catch (error) {
      console.error("❌ Error during comprehensive storage clear:", error);
      return false;
    }
  },

  // Check if localStorage is available
  isAvailable: () => {
    try {
      const test = "__localStorage_test__";
      localStorage.setItem(test, "test");
      localStorage.removeItem(test);
      return true;
    } catch (error) {
      return false;
    }
  },
};

export default storage;
